import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import { FeedbackStep, IndividualFeedback } from '../../types/steps';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { StepType } from '../../types/steps.type';
import { UserInputAreaComponent } from '../user-input-area/user-input-area.component';
import { Subject, takeUntil } from 'rxjs';
import { CustomVariable } from '../../types/chat-flow.interface';
import { CustomVariablesService } from '../ui-chat-bot-sa/custom-variables.service';
import { replaceParamVariables } from 'src/app/shared/utils/replace-variables.util';

@Component({
  selector: 'feedback-step-inputs',

  templateUrl: './feedback-step-inputs.component.html',
  styleUrl: './feedback-step-inputs.component.scss',
  standalone: false
})
export class FeedbackStepInputsComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Output() feedbackChanged = new EventEmitter<any>();
  @Input() step: FeedbackStep | undefined;
  @Input() currentLang!: string;
  @Input() errorMessage: string | undefined;
  @Input() validationAttempt: number = 0;
  readonly feedbackErrorMessageId = 'feedback-error-message';
  @ViewChildren(UserInputAreaComponent)
  inputAreas!: QueryList<UserInputAreaComponent>;
  @ViewChildren('checkboxGroup', { read: ElementRef })
  checkboxGroups!: QueryList<ElementRef<HTMLElement>>;
  dynamicVariables: CustomVariable | null = null;
  private destroy$ = new Subject<void>();

  constructor(private customVariablesService: CustomVariablesService) {}

  ngOnChanges(changes: SimpleChanges): void {
    const errorAppeared =
      !!changes['errorMessage']?.currentValue &&
      !changes['errorMessage']?.previousValue;
    const validationAttemptChanged =
      !!changes['validationAttempt'] &&
      !changes['validationAttempt'].firstChange;
    const shouldFocus =
      !!this.errorMessage && (errorAppeared || validationAttemptChanged);

    if (shouldFocus) {
      this.focusFirstInvalidField();
    }
  }

  ngOnInit(): void {
    this.customVariablesService.customVariables$
      .pipe(takeUntil(this.destroy$))
      .subscribe((variables) => {
        this.dynamicVariables = variables;
      });

    if (this.step && this.step.type === StepType.FEEDBACK) {
      this.step.additional_config?.feedback_blocks?.forEach((feedback) => {
        if (feedback.feedback_type === 'checkbox') {
          feedback.checkbox_options?.forEach((option) => {
            option.checked = false;
          });
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  onCheckboxChange(
    event: MatCheckboxChange,
    option: { checked?: boolean; [key: string]: any },
    feedbackIndex: number
  ): void {
    if (this.step?.type === StepType.FEEDBACK) {
      const isChecked = event.checked;
      // Update your option object or handle the change as needed
      option.checked = isChecked;
      if (option.checked) {
        // Push the item if checked
        this.step.api.data.feedbacks[feedbackIndex].categories?.push(
          option['message'][this.currentLang]
        );
      } else if (!option.checked) {
        // Remove the item if unchecked
        const categories =
          this.step.api.data.feedbacks[feedbackIndex].categories;
        const index = categories?.indexOf(option['message'][this.currentLang]);
        if (index !== -1) {
          categories?.splice(index!, 1); // Using the non-null assertion operator
        }
      }
      // console.log(
      //   'categories:',
      //   this.step.api.data.feedbacks[feedbackIndex].categories
      // );
    }
  }
  onFeedbackTextArea(value: string, feedbackIndex: number) {
    // Set feedback text value to the API data
    if (this.step?.type === StepType.FEEDBACK) {
      this.step.api.data.feedbacks[feedbackIndex].text = value;
    }
  }

  getFeedbackQuestionId(
    feedback: IndividualFeedback,
    feedbackIndex: number
  ): string | null {
    return feedback.message?.message?.[this.currentLang]
      ? `feedback-question-${feedbackIndex}`
      : null;
  }

  isFeedbackRequired(feedback: IndividualFeedback): boolean {
    return (
      feedback.is_required ||
      this.step?.additional_config?.required_at_least_one_feedback
        ?.answer_required === true
    );
  }

  getFeedbackInputAriaLabel(feedback: IndividualFeedback): string | null {
    const questionMessage = feedback.message?.message?.[this.currentLang];
    if (!questionMessage) {
      return null;
    }

    return replaceParamVariables(questionMessage, this.dynamicVariables)
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private focusFirstInvalidField(): void {
    if (!this.step) return;

    const feedbackBlocks = this.step.additional_config?.feedback_blocks || [];
    const feedbackData = this.step.api?.data?.feedbacks || [];

    const textInputs = this.inputAreas?.toArray() ?? [];
    const checkboxGroups = this.checkboxGroups?.toArray() ?? [];
    let textIndex = 0;
    let checkboxIndex = 0;

    for (let i = 0; i < feedbackBlocks.length; i++) {
      const block = feedbackBlocks[i];
      const isTextField =
        block.feedback_type === 'textarea' ||
        block.feedback_type === 'textfield';
      const textInput = isTextField ? textInputs[textIndex++] : undefined;
      const checkboxGroup =
        block.feedback_type === 'checkbox'
          ? checkboxGroups[checkboxIndex++]?.nativeElement
          : undefined;

      if (!this.isFeedbackRequired(block)) {
        continue;
      }

      if (!this.isFeedbackInvalid(block, feedbackData[i])) {
        continue;
      }

      if (isTextField) {
        if (textInput) {
          textInput.focus();
          return;
        }
        continue;
      }

      if (block.feedback_type === 'checkbox') {
        const firstCheckboxInput =
          checkboxGroup?.querySelector<HTMLInputElement>(
            'input[type="checkbox"]'
          );

        if (firstCheckboxInput) {
          firstCheckboxInput.focus();
          return;
        }
      }
    }
  }

  private isFeedbackInvalid(
    feedbackBlock: IndividualFeedback,
    feedbackData: { categories?: string[]; text?: string } | undefined
  ): boolean {
    if (feedbackBlock.feedback_type === 'checkbox') {
      return !feedbackData?.categories || feedbackData.categories.length === 0;
    }

    if (
      feedbackBlock.feedback_type === 'textarea' ||
      feedbackBlock.feedback_type === 'textfield'
    ) {
      return !feedbackData?.text || feedbackData.text.trim().length === 0;
    }

    return false;
  }
}
